// Toolbar bundle entry (DESIGN §4.2). Loaded with `defer`; makes no network requests.
import css from './style.scss?inline';
import { pickLanguage, readConfig } from './config';
import { LABELS } from './i18n';
import { buildPageCss, createFontScaler, createPageSheet } from './pageStyles';
import {
	defaultPrefs,
	effectivePrefs,
	sanitizePrefs,
	type Prefs,
} from './prefs';
import { createStore, STORAGE_KEY } from './storage';
import {
	localVoices,
	onVoicesChanged,
	pageText,
	speak,
	stopSpeaking,
} from './tts';
import { createUi, HOST_TAG } from './ui';

const init = (): void => {
	const cfg = readConfig( document );
	if (
		! cfg ||
		! document.body ||
		document.querySelector( HOST_TAG ) ||
		! Object.values( cfg.features ).some( Boolean )
	) {
		return;
	}
	const lang = pickLanguage( cfg, document.documentElement.lang || '' );
	const store = createStore();
	const motion = window.matchMedia?.( '(prefers-reduced-motion: reduce)' );
	const base = defaultPrefs( Boolean( motion?.matches ) );
	let prefs: Prefs = sanitizePrefs( store.read(), base );
	const setSheet = createPageSheet();
	const scaleFont = createFontScaler();

	const ui = createUi( {
		cfg,
		lang,
		labels: LABELS[ lang ],
		css: css.replace( /^\ufeff/, '' ),
		onChange: ( patch ) => {
			prefs = { ...prefs, ...patch };
			store.write( prefs );
			apply();
		},
		onReset: () => {
			stopSpeaking();
			store.clear();
			prefs = base;
			apply();
		},
		onRead: () => {
			speak( pageText(), lang, document.documentElement.lang );
		},
		onStop: stopSpeaking,
		voiceAvailable: () => localVoices( lang ).length > 0,
	} );

	const apply = (): void => {
		const eff = effectivePrefs( prefs, cfg );
		setSheet( buildPageCss( eff, cfg.dyslexiaFontUrl ) );
		scaleFont( eff.fontSize );
		ui.sync( eff );
		if ( ! eff.tts ) {
			stopSpeaking();
		}
	};

	document.body.appendChild( ui.host );
	apply();
	onVoicesChanged( () => ui.refreshVoices() );
	window.addEventListener( 'pagehide', stopSpeaking );
	window.addEventListener( 'storage', ( e ) => {
		if ( e.key === STORAGE_KEY ) {
			prefs = sanitizePrefs( store.read(), base );
			apply();
		}
	} );
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init, { once: true } );
} else {
	init();
}
